import type { Room } from '@hollowveil/shared';

/**
 * Jurassic Park - "Blue et la Gestion de la Colère"
 *
 * Personnage à aider: Blue (bébé vélociraptor)
 * Problématique: Crises de colère incontrôlables, séparé de sa mère très jeune
 *
 * Révélation: Sa colère est du CHAGRIN déguisé
 *
 * Ce que Côtelette apprend: La désescalade
 */
export const roomJurassicReveil: Room = {
  id: 'room_jurassic_reveil',
  name: 'Jurassic Park - Blue et la Gestion de la Colère',
  description: 'Un parc en semi-ruine. Les scientifiques débattent du sort de Blue, un raptor "dangereux"...',
  size: [28, 12, 28],
  floorStyle: 'grass',
  biome: 'jungle',
  palette: 'forest',
  initialWorld: 'real',
  hasSpectralLayer: true,
  weather: 'drizzle',
  weatherIntensity: 0.2,
  ambiance: {
    realWorld: {
      backgroundColor: '#4a7c59',
      ambientLightColor: '#90EE90',
      ambientLightIntensity: 0.7,
      fogColor: '#6b8e6b',
      fogDensity: 0.02,
    },
    spectralWorld: {
      // Monde émotionnel - on voit les émotions des dinosaures
      backgroundColor: '#2d1f3d',
      ambientLightColor: '#ff6b6b',
      ambientLightIntensity: 0.4,
      fogColor: '#3d2f4d',
      fogDensity: 0.04,
    },
  },
  objects: [
    // === CAGE DE BLUE ===
    {
      id: 'obj_blue_cage',
      name: 'Cage de Confinement',
      type: 'interactive',
      modelId: 'dinosaur_cage',
      position: [14, 0, 20],
      rotation: [0, 0, 0],
      scale: [3, 3, 3],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        locked: true,
        code: '1993', // Année de sortie de Jurassic Park
      },
      collider: { type: 'box', size: [6, 6] },
      onInteract: [
        {
          type: 'showRiddle',
          params: {
            riddleId: 'cage_code_puzzle',
          },
        },
      ],
    },

    // Blue le vélociraptor
    {
      id: 'obj_blue',
      name: 'Blue',
      type: 'interactive',
      modelId: 'npc_velociraptor',
      position: [14, 0, 20],
      rotation: [0, Math.PI, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        state: 'aggressive',
        angerLevel: 80,
        dialogueId: 'blue_intro',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: '*GRRRR* Blue grogne et montre les dents. Il est terrifié et en colère.',
          },
        },
      ],
    },

    // Aura de colère de Blue (spectral)
    {
      id: 'obj_blue_anger_aura',
      name: 'Aura de Colère',
      type: 'static',
      modelId: 'energy_field',
      position: [14, 1, 20],
      rotation: [0, 0, 0],
      scale: [4, 4, 4],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        color: '#ff0000',
        pulsing: true,
        emotion: 'anger',
      },
    },

    // Souvenir de sa mère (visible en spectral)
    {
      id: 'obj_mother_memory',
      name: 'Souvenir de Maman',
      type: 'static',
      modelId: 'ghost_raptor',
      position: [16, 1.5, 22],
      rotation: [0, -0.5, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        ghostly: true,
        memory: true,
      },
    },

    // === PANNEAU DE CONTRÔLE DE LA CAGE ===
    {
      id: 'obj_control_panel',
      name: 'Panneau de Contrôle',
      type: 'interactive',
      modelId: 'control_panel',
      position: [11, 0, 20],
      rotation: [0, Math.PI / 2, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'showRiddle',
          params: {
            riddleId: 'cage_code_puzzle',
          },
        },
      ],
    },

    // Dossier de Blue (avec la date de naissance pour le code)
    {
      id: 'obj_blue_file',
      name: 'Dossier de Blue',
      type: 'collectible',
      modelId: 'folder',
      position: [8, 0.8, 8],
      rotation: [0, 0.2, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Dossier #B-1993: "Blue - Vélociraptor femelle. Née le 11/06/1993. Note: Comportement agressif depuis la disparition de la mère. Code cage: 1993"',
          },
        },
      ],
    },

    // === SCIENTIFIQUES ===

    // Scientifique bienveillant
    {
      id: 'obj_scientist_kind',
      name: 'Dr. Sarah',
      type: 'interactive',
      modelId: 'npc_scientist',
      position: [6, 0, 14],
      rotation: [0, Math.PI / 4, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        dialogueId: 'scientist_kind',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Dr. Sarah: "Blue n\'est pas méchante... Elle a juste perdu sa mère quand elle était bébé. Depuis, elle ne fait plus confiance à personne."',
          },
        },
      ],
    },

    // Scientifique hostile
    {
      id: 'obj_scientist_hostile',
      name: 'Dr. Hammond Jr',
      type: 'interactive',
      modelId: 'npc_scientist',
      position: [20, 0, 14],
      rotation: [0, -Math.PI / 4, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        dialogueId: 'scientist_hostile',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Dr. Hammond Jr: "Ce raptor est DANGEREUX ! Il faut l\'éliminer avant qu\'il ne tue quelqu\'un !" *Côtelette fronce les sourcils*',
          },
        },
      ],
    },

    // === ZONE SÉCURISÉE (pour rediriger la colère) ===
    {
      id: 'obj_safe_zone',
      name: 'Zone de Décompression',
      type: 'interactive',
      modelId: 'fenced_area',
      position: [6, 0, 22],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        purpose: 'anger_management',
      },
      collider: { type: 'box', size: [4, 4] },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Une zone où Blue pourrait exprimer sa colère sans danger : un tronc à mordre, de la terre à creuser...',
          },
        },
      ],
    },

    // Tronc à mordre
    {
      id: 'obj_chewing_log',
      name: 'Tronc à Mordre',
      type: 'interactive',
      modelId: 'log',
      position: [5, 0, 23],
      rotation: [0, 0.3, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Un tronc épais... parfait pour que Blue décharge sa frustration.',
          },
        },
      ],
    },

    // === LE BÉBÉ RAPTOR (futur compagnon) ===
    {
      id: 'obj_baby_raptor',
      name: 'Bébé Raptor',
      type: 'interactive',
      modelId: 'baby_raptor',
      position: [22, 0, 8],
      rotation: [0, Math.PI, 0],
      scale: [0.6, 0.6, 0.6],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        willFollow: true,
        dialogueId: 'baby_raptor_intro',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: '*Petit cri curieux* Le bébé raptor te regarde avec de grands yeux... Il semble vouloir te suivre.',
          },
        },
      ],
    },

    // === DÉCORS ===

    // Portail d'entrée Jurassic Park
    {
      id: 'obj_jp_gate',
      name: 'Portail Jurassic Park',
      type: 'static',
      modelId: 'jp_gate',
      position: [14, 0, 2],
      rotation: [0, 0, 0],
      scale: [3, 3, 3],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      collider: { type: 'box', size: [8, 1] },
    },

    // Centre des visiteurs
    {
      id: 'obj_visitor_center',
      name: 'Centre des Visiteurs',
      type: 'static',
      modelId: 'visitor_center',
      position: [6, 0, 8],
      rotation: [0, Math.PI / 2, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      collider: { type: 'box', size: [5, 5] },
    },

    // Nid de dinosaure
    {
      id: 'obj_dino_nest',
      name: 'Nid de Dinosaure',
      type: 'interactive',
      modelId: 'dino_nest',
      position: [22, 0, 16],
      rotation: [0, 0.3, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Un nid avec des fragments de coquilles... Les raptors naissent ici.',
          },
        },
      ],
    },

    // Clôtures électriques (certaines cassées)
    {
      id: 'obj_electric_fence_1',
      name: 'Clôture Électrique',
      type: 'static',
      modelId: 'electric_fence',
      position: [2, 0, 10],
      rotation: [0, 0, 0],
      scale: [1, 1, 10],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { powered: false },
      collider: { type: 'box', size: [0.5, 10] },
    },
    {
      id: 'obj_electric_fence_2',
      name: 'Clôture Électrique Cassée',
      type: 'static',
      modelId: 'electric_fence_broken',
      position: [26, 0, 10],
      rotation: [0, 0, 0],
      scale: [1, 1, 10],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { powered: false, broken: true },
    },
    {
      id: 'obj_electric_fence_3',
      name: 'Clôture Électrique',
      type: 'static',
      modelId: 'electric_fence',
      position: [14, 0, 26],
      rotation: [0, Math.PI / 2, 0],
      scale: [1, 1, 14],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { powered: false },
    },

    // Végétation jungle dense
    {
      id: 'obj_palm_1',
      name: 'Palmier',
      type: 'static',
      modelId: 'palm_tree',
      position: [4, 0, 4],
      rotation: [0, 0.3, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_palm_2',
      name: 'Palmier',
      type: 'static',
      modelId: 'palm_tree',
      position: [24, 0, 4],
      rotation: [0, -0.5, 0],
      scale: [2.2, 2.2, 2.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_palm_3',
      name: 'Palmier',
      type: 'static',
      modelId: 'palm_tree',
      position: [2, 0, 20],
      rotation: [0, 1.2, 0],
      scale: [1.8, 1.8, 1.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_palm_4',
      name: 'Palmier',
      type: 'static',
      modelId: 'palm_tree',
      position: [26, 0, 22],
      rotation: [0, -0.8, 0],
      scale: [2.5, 2.5, 2.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_fern_1',
      name: 'Fougère Géante',
      type: 'static',
      modelId: 'giant_fern',
      position: [10, 0, 6],
      rotation: [0, 0.1, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_fern_2',
      name: 'Fougère Géante',
      type: 'static',
      modelId: 'giant_fern',
      position: [18, 0, 12],
      rotation: [0, 0.7, 0],
      scale: [1.3, 1.3, 1.3],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_fern_3',
      name: 'Fougère Géante',
      type: 'static',
      modelId: 'giant_fern',
      position: [4, 0, 24],
      rotation: [0, -0.3, 0],
      scale: [1.8, 1.8, 1.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_banana_tree_1',
      name: 'Bananier',
      type: 'static',
      modelId: 'banana_tree',
      position: [8, 0, 24],
      rotation: [0, 0.5, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_tropical_flower_1',
      name: 'Fleur Tropicale',
      type: 'static',
      modelId: 'tropical_flower',
      position: [12, 0, 4],
      rotation: [0, 0.2, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { color: '#ff6b9d' },
    },
    {
      id: 'obj_tropical_flower_2',
      name: 'Fleur Tropicale',
      type: 'static',
      modelId: 'tropical_flower',
      position: [20, 0, 10],
      rotation: [0, -0.4, 0],
      scale: [1.0, 1.0, 1.0],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { color: '#ffa500' },
    },

    // Rochers
    {
      id: 'obj_rock_1',
      name: 'Rocher',
      type: 'static',
      modelId: 'rock',
      position: [2, 0, 6],
      rotation: [0, 0.3, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_rock_2',
      name: 'Rocher',
      type: 'static',
      modelId: 'rocks',
      position: [26, 0, 18],
      rotation: [0, -0.5, 0],
      scale: [2.5, 2.5, 2.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Jeep Jurassic Park
    {
      id: 'obj_jeep',
      name: 'Jeep Jurassic Park',
      type: 'static',
      modelId: 'jp_jeep',
      position: [18, 0, 6],
      rotation: [0, Math.PI / 4, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      collider: { type: 'box', size: [4, 2] },
    },
    {
      id: 'obj_jeep_2',
      name: 'Jeep Jurassic Park',
      type: 'static',
      modelId: 'jp_jeep',
      position: [6, 0, 18],
      rotation: [0, Math.PI + 0.3, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      collider: { type: 'box', size: [4, 2] },
    },

    // Portail retour vers le Sanctuaire
    {
      id: 'obj_portal_back',
      name: 'Portail vers le Sanctuaire',
      type: 'interactive',
      modelId: 'portal_sanctuary',
      position: [14, 0, 1],
      rotation: [0, 0, 0],
      scale: [2, 3, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        portalTo: 'room_sanctuary',
        portalColor: '#9d8cff',
      },
      onInteract: [
        {
          type: 'loadRoom',
          params: { roomId: 'room_sanctuary' },
        },
      ],
    },

    // L'Oracle perché dans un arbre
    {
      id: 'obj_oracle_appearance',
      name: 'L\'Oracle Chevelu',
      type: 'interactive',
      modelId: 'npc_oracle_chevelu',
      position: [24, 2, 18],
      rotation: [0, -Math.PI / 2, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        dialogueId: 'oracle_jurassic_hint',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Oracle: *perché sur un arbre* "Ma vision est claire ! Ce dinosaure a besoin de quelqu\'un qui ne le regarde pas comme un monstre." *Blue le fixe* "...Aussi, peux-tu faire vite ? Il regarde mes cheveux bizarrement."',
          },
        },
      ],
    },
  ],
  puzzles: [
    // Ouvrir la cage
    {
      id: 'puzzle_open_cage',
      name: 'Code de la Cage',
      type: 'riddles',
      state: 'active',
      description: 'Trouve le code pour ouvrir la cage de Blue.',
      hint: 'Cherche le dossier de Blue quelque part dans le parc.',
      linkedObjectIds: ['obj_control_panel', 'obj_blue_file'],
      solution: {
        type: 'state',
        expected: { code: '1993' },
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: '*CLANG* La cage s\'ouvre ! Blue est libre... mais toujours en colère.',
          },
        },
      ],
    },
    // Désescalade
    {
      id: 'puzzle_deescalation',
      name: 'Calmer Blue',
      type: 'sequence',
      state: 'locked',
      description: 'Calme Blue sans l\'affronter. Utilise les bons gestes au bon moment.',
      hint: 'Recule, parle doucement, ne montre pas de peur.',
      linkedObjectIds: ['obj_blue'],
      solution: {
        type: 'sequence',
        expected: ['back_off', 'soft_voice', 'show_memory', 'wait', 'approach_slowly'],
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: 'Blue se calme... Elle pose sa tête contre ta main. Elle n\'est pas un monstre. Elle avait juste besoin qu\'on la comprenne.',
          },
        },
        {
          type: 'addToInventory',
          params: {
            itemId: 'griffe_resilience',
            itemName: 'Griffe de Résilience',
            itemDescription: 'Une griffe de Blue. Symbole que la force peut être contrôlée.',
            itemIcon: 'key',
          },
        },
      ],
    },
  ],
  connections: [
    {
      targetRoomId: 'room_sanctuary',
      position: [14, 0, 1],
      direction: 'north',
      locked: false,
    },
  ],
  npcs: [
    // Bébé raptor (celui qui devient le compagnon)
    {
      id: 'npc_baby_raptor',
      type: 'lizard', // Utilise lizard comme base
      name: 'Bébé Raptor',
      position: [22, 0, 8],
      scale: 0.6,
      idleAnimation: 'stand',
      petAnimation: 'happy',
      petRadius: 2,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Brachiosaure (géant, on caresse sa patte)
    {
      id: 'npc_brachiosaurus',
      type: 'camel', // Utilise camel comme base pour la taille
      name: 'Brachiosaure',
      position: [8, 0, 26],
      scale: 3,
      idleAnimation: 'graze',
      petAnimation: 'nuzzle',
      petRadius: 4,
      petCooldown: 5000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Tricératops
    {
      id: 'npc_triceratops',
      type: 'goat', // Utilise goat comme base (cornes)
      name: 'Tricératops',
      position: [20, 0, 26],
      scale: 2,
      idleAnimation: 'graze',
      petAnimation: 'happy',
      petRadius: 3,
      petCooldown: 4000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Compy mignon
    {
      id: 'npc_compy',
      type: 'bird', // Utilise bird comme base (petit)
      name: 'Compy',
      position: [12, 0, 10],
      scale: 0.4,
      idleAnimation: 'stand',
      petAnimation: 'chirp',
      petRadius: 1.5,
      petCooldown: 2000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Ptéranodon (volant)
    {
      id: 'npc_pteranodon',
      type: 'bird',
      name: 'Ptéranodon',
      position: [14, 4, 14],
      scale: 2,
      idleAnimation: 'fly',
      petAnimation: 'happy',
      petRadius: 3,
      petCooldown: 4000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Stégosaure pacifique
    {
      id: 'npc_stegosaurus',
      type: 'camel',
      name: 'Stégosaure',
      position: [4, 0, 22],
      scale: 2.2,
      idleAnimation: 'graze',
      petAnimation: 'nuzzle',
      petRadius: 3.5,
      petCooldown: 4000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Dilophosaure curieux (le petit qui crache)
    {
      id: 'npc_dilophosaurus',
      type: 'lizard',
      name: 'Dilophosaure',
      position: [24, 0, 12],
      scale: 1.2,
      idleAnimation: 'stand',
      petAnimation: 'happy',
      petRadius: 2.5,
      petCooldown: 3500,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Gallimimus rapide
    {
      id: 'npc_gallimimus',
      type: 'bird',
      name: 'Gallimimus',
      position: [16, 0, 16],
      scale: 1.5,
      idleAnimation: 'stand',
      petAnimation: 'happy',
      petRadius: 2.5,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'both',
    },
  ],
};
